import * as THREE from "three";
import type { OreExhibit } from "./ore-exhibit";

const tones = {
  blue: 0x348bc2,
  teal: 0x319585,
  amber: 0xe6ad39,
  rose: 0xc66667,
};
const surface = (color: number) =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.72, metalness: 0.08 });
const block = (
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
) => {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
};

/** A magnified architectural cutaway. Exhibit labels are DOM for crisp, accessible text. */
export class OptimizerInterior {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(42, 1, 0.1, 140);
  private readonly hall = new THREE.Group();
  private readonly roof = new THREE.Group();
  private readonly doors = new THREE.Group();
  private exhibits = new THREE.Group();
  private readonly server = new THREE.Group();
  private markers: THREE.Mesh[] = [];
  private mechanisms: THREE.Group[] = [];
  private connections: { object: THREE.Mesh; target: number }[] = [];
  private positions: THREE.Vector3[] = [];
  private labels: HTMLElement[] = [];
  private exhibit: OreExhibit | null = null;
  private width = 1000;
  private height = 650;
  private compact = false;
  private previousFocus = -1;
  private readonly scratch = new THREE.Vector3();

  constructor(
    private readonly labelLayer: HTMLElement,
    private readonly onFocus: (text: string) => void,
  ) {
    this.scene.background = new THREE.Color(0xe5eef1);
    this.scene.add(new THREE.HemisphereLight(0xe9f8ff, 0xb8af92, 1.8));
    const sun = new THREE.DirectionalLight(0xfff1d8, 2.2);
    sun.position.set(-8, 18, 12);
    this.scene.add(sun);
    const stone = surface(0xf3ead9),
      glass = surface(0x94c6dc),
      trim = surface(0xb5ccd3);
    this.hall.add(block(25, 0.4, 25, surface(0xc7d6ce), 0, -0.35));
    this.hall.add(block(25, 6.5, 0.4, stone, 0, 3, -12));
    for (const x of [-10, -6, -2, 2, 6, 10]) {
      this.hall.add(block(2.9, 3.8, 0.15, glass, x, 3.2, -11.72));
      this.hall.add(block(0.22, 5.8, 0.4, trim, x - 1.65, 2.8, -11.5));
    }
    for (const x of [-12, 12]) {
      for (const z of [-8, -1, 6]) {
        this.hall.add(block(0.55, 6, 0.55, stone, x, 2.8, z));
        this.hall.add(block(1, 0.25, 1, trim, x, 5.8, z));
      }
      this.hall.add(block(0.45, 0.4, 22, trim, x, 6, -1));
    }
    // Front wings and skylight lift out of the section, rather than fading all geometry.
    this.doors.add(block(7.8, 5.8, 0.4, stone, -8, 2.8, 10));
    this.doors.add(block(7.8, 5.8, 0.4, stone, 8, 2.8, 10));
    this.doors.add(block(24, 0.7, 0.6, trim, 0, 5.9, 10));
    for (const x of [-8, -4, 0, 4, 8])
      this.roof.add(block(0.22, 0.35, 22, trim, x, 6.4, -1));
    this.roof.add(block(24, 0.2, 2, stone, 0, 6.4, -10));
    this.hall.add(this.doors, this.roof);
    for (const x of [-10.5, 10.5])
      for (const z of [-8.5, 5]) {
        this.hall.add(block(1.5, 0.6, 1.5, surface(0xc3b49d), x, 0.2, z));
        const tree = new THREE.Mesh(
          new THREE.IcosahedronGeometry(0.95, 1),
          surface(0x65977b),
        );
        tree.position.set(x, 1.35, z);
        this.hall.add(tree);
      }
    const floorLine = surface(0xc7d7da);
    for (let i = -10; i <= 10; i += 2) {
      this.hall.add(block(0.025, 0.012, 24, floorLine, i, -0.13));
      this.hall.add(block(24, 0.012, 0.025, floorLine, 0, -0.13, i));
    }
    this.server.add(block(0.62, 0.4, 1.1, surface(0xf6c344)));
    this.server.add(block(0.48, 0.25, 0.4, surface(0x264e68), 0, 0.3, 0));
    const beacon = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, 0.65, 12),
      surface(0xe5ab30),
    );
    beacon.position.y = 0.7;
    this.server.add(beacon);
    this.scene.add(this.hall, this.exhibits, this.server);
  }

  resize(width: number, height: number): void {
    const compact = width < 620;
    this.width = width;
    this.height = height;
    this.labelLayer.classList.toggle(
      "interior-tight",
      height < 620 || (width < 620 && height < 820),
    );
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    if (compact !== this.compact) {
      this.compact = compact;
      if (this.exhibit) this.setExhibit(this.exhibit);
    }
  }

  setExhibit(exhibit: OreExhibit): void {
    this.disposeExhibits();
    this.exhibit = exhibit;
    this.previousFocus = -1;
    this.positions = exhibit.nodes.map((_, i) => {
      if (this.compact)
        return new THREE.Vector3(
          0,
          0,
          (i - (exhibit.nodes.length - 1) / 2) * 5.2,
        );
      if (exhibit.nodes.length === 4)
        return new THREE.Vector3(
          i % 2 === 0 ? -5.4 : 5.4,
          0,
          i < 2 ? -4.8 : 4.5,
        );
      return new THREE.Vector3(
        i === 2 ? 0 : i === 0 ? -5.4 : 5.4,
        0,
        i === 2 ? 4.5 : -4.8,
      );
    });
    this.labels = exhibit.nodes.map((n, i) => {
      const position = this.positions[i]!;
      const podium = block(
        this.compact ? 6 : 6.3,
        0.6,
        4.2,
        surface(0xc4d0c6),
        position.x,
        0.2,
        position.z,
      );
      this.exhibits.add(podium);
      const marker = block(
        0.6,
        0.8,
        0.6,
        surface(tones[n.tone]),
        position.x - 2,
        0.9,
        position.z + 0.3,
      );
      this.exhibits.add(marker);
      this.markers.push(marker);
      this.exhibits.add(
        block(
          5.2,
          0.08,
          0.14,
          surface(tones[n.tone]),
          position.x,
          0.56,
          position.z + 1.2,
        ),
      );
      const mechanism = new THREE.Group();
      mechanism.position.copy(position).add(new THREE.Vector3(0, 0.55, -0.8));
      const color = surface(tones[n.tone]);
      const pale = surface(0xece4cf);
      // Raised query-block tiles and a two-input junction: compilation artifacts,
      // deliberately unlike the SELECT journey's flying physical data blocks.
      for (const side of [-1, 1]) {
        mechanism.add(block(1.3, 0.16, 0.9, pale, side * 1.55, 0.3, 0));
        mechanism.add(block(1.1, 0.16, 0.75, color, side * 1.55, 0.5, -0.08));
        mechanism.add(block(1.15, 0.08, 0.1, color, side * 0.7, 0.3, -0.5));
      }
      mechanism.add(block(0.6, 1.3, 0.6, color, 0, 0.65, -0.5));
      mechanism.add(block(2.8, 0.15, 0.2, pale, 0, 1.5, -0.5));
      for (const x of [-1.3, 1.3])
        mechanism.add(block(0.14, 1.5, 0.2, pale, x, 0.75, -0.5));
      mechanism.scale.setScalar(this.compact ? 0.62 : 1);
      if (this.compact) mechanism.position.x += 2.5;
      this.exhibits.add(mechanism);
      this.mechanisms.push(mechanism);
      const label = document.createElement("div");
      label.className = `exhibit-label tone-${n.tone}`;
      const tag = document.createElement("span");
      tag.textContent = `${String(i + 1).padStart(2, "0")} / ${exhibit.nodes.length} · ${n.tag}`;
      const title = document.createElement("strong");
      title.textContent = n.title;
      const detail = document.createElement("p");
      detail.textContent = n.detail;
      label.append(tag, title, detail);
      this.labelLayer.append(label);
      return label;
    });
    for (const [from, to] of exhibit.links) {
      const a = this.positions[from]!,
        b = this.positions[to]!;
      const points = [
        a.clone().setY(0.08),
        new THREE.Vector3(a.x, 0.08, (a.z + b.z) / 2),
        new THREE.Vector3(b.x, 0.08, (a.z + b.z) / 2),
        b.clone().setY(0.08),
      ];
      const path = new THREE.CatmullRomCurve3(points, false, "centripetal");
      const line = new THREE.Mesh(
        new THREE.TubeGeometry(path, 32, 0.045, 5, false),
        new THREE.MeshBasicMaterial({ color: 0x568994 }),
      );
      this.exhibits.add(line);
      this.connections.push({ object: line, target: to });
      const arrow = new THREE.Mesh(
        new THREE.ConeGeometry(0.18, 0.5, 4),
        new THREE.MeshBasicMaterial({ color: 0x377286 }),
      );
      arrow.position.copy(path.getPoint(0.65));
      arrow.quaternion.setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        path.getTangent(0.65),
      );
      this.exhibits.add(arrow);
    }
    this.scene.add(this.exhibits);
  }

  update(progress: number, reducedMotion: boolean): void {
    if (!this.exhibit) return;
    const open = reducedMotion
      ? 1
      : THREE.MathUtils.smoothstep(progress, 0, 0.25);
    this.doors.position.y = open * 16;
    this.roof.position.y = open * 16;
    this.doors.visible = open < 1;
    this.roof.visible = open < 1;
    const halfWidth = this.compact ? 6.7 : 14.8;
    const distance = Math.max(
      halfWidth / Math.tan(THREE.MathUtils.degToRad(21)) / this.camera.aspect,
      this.compact ? 31 : 31,
    );
    this.camera.position.set(
      0,
      distance * (0.38 + open * 0.28),
      distance * (0.92 - open * 0.17),
    );
    this.camera.lookAt(0, 0, 0);
    this.camera.updateMatrixWorld();
    const work = THREE.MathUtils.clamp((progress - 0.22) / 0.78, 0, 1);
    const sequence = this.exhibit.focus;
    const index = Math.min(
      sequence.length - 1,
      Math.floor(work * sequence.length),
    );
    const focus = sequence[index]!;
    const next = this.positions[focus]!;
    const previous =
      index > 0
        ? this.positions[sequence[index - 1]!]!
        : new THREE.Vector3(0, 0, 10);
    const travel = reducedMotion
      ? 1
      : THREE.MathUtils.smoothstep((work * sequence.length) % 1, 0, 0.32);
    this.server.position.copy(previous).lerp(next, progress >= 1 ? 1 : travel);
    this.server.position.add(new THREE.Vector3(-2.5, 1, 1.7));
    this.server.rotation.y = Math.atan2(
      next.x - previous.x,
      next.z - previous.z,
    );
    this.labelLayer.style.opacity = open > 0.75 ? "1" : "0";
    this.positions.forEach((position, i) => {
      this.scratch
        .copy(position)
        .add(
          this.compact
            ? new THREE.Vector3(0, 2.4, 0)
            : new THREE.Vector3(0, 0.9, 1.05),
        )
        .project(this.camera);
      const label = this.labels[i]!;
      label.style.left = `${(this.scratch.x * 0.5 + 0.5) * this.width}px`;
      label.style.top = `${(-this.scratch.y * 0.5 + 0.5) * this.height}px`;
      label.classList.toggle("is-current", focus === i);
      label.classList.toggle("is-visited", index > sequence.indexOf(i));
      this.markers[i]!.scale.y = i === focus ? 1.5 : 1;
      const reveal = i <= index;
      this.mechanisms[i]!.visible = reveal;
      if (reveal) {
        const expansion =
          i === focus && !reducedMotion
            ? THREE.MathUtils.smoothstep(
                (work * sequence.length) % 1,
                0.25,
                0.7,
              )
            : 1;
        this.mechanisms[i]!.scale.x =
          (this.compact ? 0.62 : 1) *
          (0.55 + 0.45 * (progress >= 1 ? 1 : expansion));
      }
    });
    for (const connection of this.connections)
      (connection.object.material as THREE.MeshBasicMaterial).color.setHex(
        connection.target === focus ? 0xc59428 : 0x568994,
      );
    if (focus !== this.previousFocus) {
      this.previousFocus = focus;
      this.onFocus(
        `Server 1 · station ${focus + 1}/${sequence.length} · ${this.exhibit.nodes[focus]!.title}`,
      );
    }
  }

  dispose(): void {
    this.disposeExhibits();
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    this.scene.traverse((o) => {
      if (o instanceof THREE.Mesh || o instanceof THREE.Line) {
        geometries.add(o.geometry);
        (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) =>
          materials.add(m),
        );
      }
    });
    geometries.forEach((g) => g.dispose());
    materials.forEach((m) => m.dispose());
    this.labelLayer.remove();
  }

  private disposeExhibits(): void {
    this.exhibits.removeFromParent();
    this.exhibits.traverse((o) => {
      if (o instanceof THREE.Mesh || o instanceof THREE.Line) {
        o.geometry.dispose();
        const materials = Array.isArray(o.material) ? o.material : [o.material];
        materials.forEach((m) => m.dispose());
      }
    });
    this.exhibits = new THREE.Group();
    this.markers = [];
    this.mechanisms = [];
    this.connections = [];
    this.labels.forEach((l) => l.remove());
    this.labels = [];
  }
}
